<template>
  <div>
    <admin-wrapper
      :straight-title="vehiclesModuleName"
      :inputs="inputs"
      :has-buttons="false"
      :hide-title-padding="!vehiclesHistoryModuleEnabled"
    >
      <template #greyTabs.input>
        <grey-tabs v-model="tab" :tabs="tabs" has-border cols="auto" :hide-header="!vehiclesHistoryModuleEnabled">
          <v-tab-item>
            <admin-wrapper
              hide-title-row
              :inputs="vehiclesInputs"
              :moduleEnabler="vehiclesModuleEnabler"
              :is-form-fetched="isFormFetched"
              @save="saveCard"
            >
              <template #vehicles_api_provider_name.input.inner>
                <v-row no-gutters>
                  <v-col cols="auto">
                    <span class="text-body-2">
                      {{ vehiclesProvider }}
                    </span>
                  </v-col>
                </v-row>
              </template>
              <template #featureCard.input>
                <draggable-lists-manage-card
                  :layerId="vehiclesLayerId"
                  @update:hasAnyRequiredOnRightSide="hasAnyRequiredOnRightSide = $event"
                ></draggable-lists-manage-card>
              </template>
              <template #symbolization.input>
                <base-button
                  @click="openSymbolizationDialog"
                  translation-path="menu.changeSymbolization"
                  :loading="isStyleDialogOpening"
                />
              </template>
            </admin-wrapper>
          </v-tab-item>
          <v-tab-item v-if="vehiclesHistoryModuleEnabled">
            <admin-wrapper
              hide-title-row
              :inputs="vehiclesHistoryInputs"
              :moduleEnabler="vehiclesHistoryModuleEnabler"
            />
          </v-tab-item>
        </grey-tabs>
      </template>
    </admin-wrapper>
    <style-dialog
      v-if="isStyleDialogLoaded"
      :layer="currentLayerStyleEdit"
      :attributes="currentLayerStyleEditAttributes"
      :geomType="currentLayerStyleEditGeometryType"
      :is-visible.sync="isStyleDialogVisible"
      :loading="isStyleButtonLoading"
      @acceptStyle="setLayerStyle"
      scope="module"
      is-admin
    />
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DraggableListsManageCard from '@/components/DraggableListsManageCard';
import GreyTabs from '@/components/GreyTabs';

export default {
  name: 'Vehicles',
  components: {
    AdminWrapper,
    DraggableListsManageCard,
    StyleDialog: () => import('@/components/StyleDialog'),
    GreyTabs,
  },
  data: () => ({
    loading: false,
    isFormFetched: false,
    isMailSent: false,
    address_to: undefined,
    hasAnyRequiredOnRightSide: false,
    isStyleDialogLoaded: false,
    isStyleButtonLoading: false,
    isStyleDialogVisible: false,
    isStyleDialogOpening: false,
    currentLayerStyleEditAttributes: undefined,
    currentLayerStyleEditGeometryType: undefined,
    currentLayerStyleEdit: undefined,
    tab: 0,
  }),
  computed: {
    vehiclesModuleName: get('admin/additionalModules@VEHICLES.verbose_name'),
    vehiclesHistoryModuleName: get('admin/additionalModules@VEHICLES_HISTORY.verbose_name'),
    vehiclesHistoryModuleEnabled: get('admin/additionalModules@VEHICLES_HISTORY.enabled'),
    vehiclesHistoryModuleEnabler() {
      return {
        name: 'vehicles_module_location_history_enabled',
        straightText: this.vehiclesHistoryModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.vehiclesHistoryModuleName }),
      };
    },
    vehiclesModuleEnabler() {
      return {
        name: 'vehicles_module_enabled',
        straightText: this.vehiclesModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.vehiclesModuleName }),
      };
    },
    vehiclesHistoryInputs() {
      return [
        {
          name: 'vehicles_history_localization_color',
          translationPath: 'navbar.symbolization',
          subtitle: 'admin.vehiclesHistorySymbolizationSubtitle',
          dataType: {
            name: 'color',
          },
          align: 'center',
          inputCols: 'auto',
          inputColClasses: ['pl-3'],
        },
      ];
    },
    vehiclesInputs() {
      return [
        {
          name: 'vehicles_api_provider_name',
          translationPath: 'admin.providerName',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'vehicles_module_locations_refresh_interval',
          clearable: true,
          align: 'start',
          translationPath: 'admin.refreshInterval',
          rules: [v => v === 0 || Boolean(v) || 'rules.required'],
          subtitle: 'admin.setRefreshInterval',
          allowNegative: false,
          minValue: 1,
          maxValue: 25,
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'featureCard',
          clearable: true,
          translationPath: 'admin.featureCard',
          isCustom: true,
          subtitle: this.$i18n.t('admin.setFeatureCard', { type: this.$i18n.t('admin.objectType') }),
          align: 'start',
        },
        {
          name: 'symbolization',
          translationPath: 'admin.defaultSymbolization',
          isCustom: true,
          subtitle: 'admin.vehicleSymbolizationSubtitle',
          align: 'start',
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'greyTabs',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
      ];
    },
    tabs() {
      return [
        { name: this.$i18n.t('admin.currentData') },
        ...(this.vehiclesHistoryModuleEnabled ? [{ name: this.$i18n.t('admin.historyData') }] : []),
      ];
    },
    vehiclesLayer: get('layers/featuresLayers@:vehiclesLayerId'),
    vehiclesProvider: get('admin/modulesMapping@vehicles.provider_name'),
    settingsValues: get('admin/settingsValues'),
    currentLayerSchema: get('layers/currentLayerSchema'),
    appName: get('admin/settingsValues@app_name'),
    isModuleEnabled: get('admin/settingsValues@vehicles_module_enabled'),
    vehiclesMapping: get('admin/modulesMapping@vehicles'),
    vehiclesDataSource() {
      if (this.vehiclesMapping) {
        return this.vehiclesMapping.datasource_name;
      }
      return undefined;
    },
    vehiclesLayerId() {
      if (this.vehiclesMapping) {
        return this.vehiclesMapping.layer_id;
      }
      return undefined;
    },
  },
  methods: {
    editLayerAction: call('layers/editLayer'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    getDataSourceForm: call('layers/getDataSourceForm'),
    editLayer: call('layers/editLayer'),
    getModulesMapping: call('admin/getModulesMapping'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    async init() {
      await this.getModulesMapping();
      await Promise.all([
        this.getDataSourceForm(this.vehiclesDataSource),
        this.getFeaturesLayer(this.vehiclesLayerId),
        this.getLayerMetadata({ dataSource: this.vehiclesDataSource }),
      ]);
      this.isFormFetched = true;
    },
    async openSymbolizationDialog() {
      this.isStyleDialogOpening = true;
      await this.getFeaturesLayer(this.vehiclesLayerId);
      const l = this.vehiclesLayer;
      this.currentLayerStyleEditGeometryType = l.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(
        JSON.stringify({
          ...l,
          style: l.style_web,
          layer_id: l.id,
        })
      );
      const value = await Promise.resolve(this.getLayerMetadata({ dataSource: l.data_source_name }));
      this.currentLayerStyleEditAttributes = value.metadata.attributes_schema.attributes
        .filter(a => a.data_type.name !== 'geometric')
        .map(a => {
          const isRelational = Boolean(a.relation);
          const dataType = a.data_type;
          const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
          return {
            isRelational,
            valueLabel: isRelational ? `mvt_rel_${a.name}` : a.name,
            valueMvt: isDate ? `mvt_int_${a.name}` : a.name,
            value: a.name,
            text: a.verbose_name,
            dataType,
          };
        });
      this.isStyleDialogOpening = false;
      this.isStyleDialogVisible = true;
      this.isStyleDialogLoaded = true;
    },
    async setLayerStyle(e) {
      try {
        this.isStyleButtonLoading = true;
        const { layer_id, style } = e;
        await this.editLayerAction({
          layer_id,
          layer_type: 'features_layers',
          body: {
            style_web: style,
          },
        });
        this.isStyleButtonLoading = false;
      } catch (error) {
        console.log('handleBlockUser -> error', error);
        this.isStyleButtonLoading = false;
      }
      this.isStyleDialogVisible = false;
    },
    async saveCard() {
      await this.editLayer({
        layer_type: 'features_layers',
        layer_id: this.vehiclesLayerId,
        body: {
          form_schema: {
            elements: this.currentLayerSchema,
          },
        },
      });
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(all, 2px);
  border-style: dashed;
}
</style>
